import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react'

import {
    PermissionSuffix,
    TenantCreateDto,
    TenantUpdateDto,
    TenantUserCreateDto,
    TenantUserUpdateDto,
} from '../../_generated/api/user-management/index'
import { useArchiveContext } from './ArchiveContext'
import DataContextValue from './DataContextValue'
import { useApiContext } from './ApiContext'
import { useAuthContext } from './AuthContext'
import { useToastContext } from './ToastContext'
import User, { Me } from '../../models/User'
import Organization from '../../models/Organization'
import TableControl from '../../models/TableControl'
import { useProvideContext } from '../../hooks/context'
import { useMutableList } from '../../hooks/mutable'
import { useImmutableObject } from '../../hooks/immutable'
import { usePaginatedTable } from '../../hooks/pagination'
import { useAction } from '../../hooks/action'
import {
    ok,
    throwIfResponseNotOk,
    mapReadListResponse,
    mapReadTableResponse,
} from '../../tools/api'
import { GenericStorage } from '../../tools/dom/storage'
import t from '../../tools/t'

export interface UserContextValue extends DataContextValue {
    me?: Me
    meLoading: boolean
    meError: Error | undefined
    refreshMe: () => Promise<void>
    can: (permission: PermissionSuffix) => boolean

    resetPassword: (userId: string) => Promise<boolean>

    users: TableControl<User>
    usersLoading: boolean
    usersError: Error | undefined
    createUser: (data: TenantUserCreateDto) => Promise<User | undefined>
    createUserLoading: boolean
    createUserError?: Error | undefined
    updateUser: (userId: string, data: TenantUserUpdateDto) => Promise<User | undefined>
    updateUserLoading: boolean
    updateUserError?: Error | undefined
    archiveUser: (userId?: string) => Promise<void>
    restoreUser: (userId?: string) => Promise<void>
    getUserNameById: (id: string) => string
    getUserProfileById: (id?: string) => User | undefined
    getOrgProfileById: (id?: string) => Organization | undefined

    selectedOrganization?: Organization
    organizations: Organization[]
    organizationsLoading: boolean
    organizationsError?: Error | undefined
    allOrganizations: TableControl<Organization>
    allOrganizationsLoading: boolean
    allOrganizationsError: Error | undefined
    createOrganization: (org: TenantCreateDto) => Promise<Organization | undefined>
    createOrganizationLoading: boolean
    createOrganizationError?: Error | undefined
    updateOrganization: (orgId: string, data: TenantUpdateDto) => Promise<Organization | undefined>
    updateOrganizationLoading: boolean
    updateOrganizationError?: Error | undefined
    selectOrganization: (orgId: string) => void
    archiveOrganization: (organizationId?: string) => Promise<void>
    restoreOrganization: (organizationId?: string) => Promise<void>

    /**
     * Convenient, non-null organization identity, which can safely be used after startup
     *  to reduce number of safeguards in requests using the selected tenant identity.
     * The lazy id may be used in event handlers and request actions, but must not be used as part of an effect hook,
     *  as it may still be unassigned and can cause implicite-dependency bugs.
     * Instead for effects, check `selectedOrganization?.id` and only send the request if one is set.
     */
    lazySelectedOrganizationId: string
}

// NOTE: All users must always have all read permissions.
//  To achieve this, we manually insert read permissions for any permission update if they don't exist.
// NOTE: Setting an empty array will effectively erase that users permissions,
//  so we should skip using this function for users that do not declare a permission set.
const enrichUserPermissions = (permissions: string[]): string[] =>
    Array.from(
        new Set([
            ...permissions,
            ...Object.values(PermissionSuffix).filter((p) => p.includes(':read')),
        ])
    )

const UserContext = createContext<UserContextValue | undefined>(undefined)

const selectedOrganizationStorage = new GenericStorage<string>('SELECTED_ORGANIZATION')

export default function ({ children }: PropsWithChildren) {
    const { logout, isLoggedIn, auth0User } = useAuthContext()
    const { userApi, organizationApi, identityApi } = useApiContext()
    const { includeArchived } = useArchiveContext()
    const Toast = useToastContext()
    const [selectedOrganization, setSelectedOrganization] = useState<Organization>()

    const lazySelectedOrganizationId = selectedOrganization?.id ?? ''

    const [me, meLoading, meError, refreshMe] = useImmutableObject(async () => {
        const orgId = selectedOrganization?.id
        if (!(orgId && isLoggedIn)) return null
        const identityResponse = await identityApi.selfGetV2()
        throwIfResponseNotOk(identityResponse)
        return new Me(identityResponse.data, auth0User)
    }, [selectedOrganization, auth0User, isLoggedIn])

    const [
        organizations,
        organizationsLoading,
        organizationsError,
        refreshOrganizations,
        addOrganization,
        replaceOrganization,
    ] = useMutableList(async () => {
        if (!isLoggedIn) return []
        const response = await identityApi.selfGetAccessibleTenantsV2()
        if (!ok(response)) {
            const result = confirm(
                'We could not find your organization access in the system. ' +
                    'This is either because you are not part of an organization, or because the service is undergoing maintenance.' +
                    '\nIf the problem persists, please contact your system administrator.' +
                    '\nYou can try logging out and back in again to get a new access token. Do you want to try that now?'
            )
            if (result) logout()
            return []
        }
        return mapReadListResponse(response, Organization)
    }, [isLoggedIn, me])

    const [
        allOrganizations,
        allOrganizationsLoading,
        allOrganizationsError,
        refreshAllOrganizations,
    ] = usePaginatedTable(
        async (table: TableControl<Organization>) => {
            if (!isLoggedIn) return null
            if (!me?.isAdmin) return
            const response = await organizationApi.getAllTenantsV2({ includeArchived })
            return mapReadTableResponse(response, table, Organization)
        },
        [isLoggedIn, me, includeArchived]
    )

    const [users, usersLoading, usersError, refreshUsers] = usePaginatedTable(
        async (table: TableControl<User>) => {
            const orgId = selectedOrganization?.id
            if (!orgId) return null
            const response = await userApi.tenantUserGetAllV2({
                tenantId: orgId,
                sortBy: table.sortBy,
                ascending: table.ascending,
                skip: table.skip,
                take: table.take,
                includeArchived,
            })
            throwIfResponseNotOk(response)
            return mapReadTableResponse(response, table, User)
        },
        [selectedOrganization, isLoggedIn, includeArchived]
    )

    const [allUserProfiles, profilesLoading, profilesError, refreshProfiles] = usePaginatedTable(
        async (table: TableControl<User>) => {
            const orgId = selectedOrganization?.id
            if (!orgId) return null
            const { skip, take } = TableControl.infinite
            const response = await userApi.tenantUserGetAllV2({
                tenantId: orgId,
                skip,
                take,
                includeArchived: true,
            })
            throwIfResponseNotOk(response)
            return mapReadTableResponse(response, table, User)
        },
        [selectedOrganization, isLoggedIn, includeArchived]
    )

    const [userDirectory, directoryLoading, directoryError, refreshUserDirectory] =
        useImmutableObject(async () => {
            // Get and map user directory to a performant dictionary look-up
            const orgId = selectedOrganization?.id
            if (!orgId) return {}
            const response = await userApi.userNameResolveV2({ tenantId: orgId })
            if (!ok(response)) return {}
            return response.data.reduce(
                (directory, user) => ({ ...directory, [user.id]: user.name }),
                {} as Record<string, string>
            )
        }, [selectedOrganization])

    useEffect(() => {
        if (selectedOrganization || !organizations.length) return
        let selected = organizations[0]
        const selectedId = selectedOrganizationStorage.read()
        if (selectedId) {
            const preselected = organizations.find((o) => o.id === selectedId)
            if (preselected) selected = preselected
        }
        if (!selected) {
            const mainAccess = me?.ownerTenantId
            const mainOrganization = organizations.find((o) => o.id === mainAccess)
            if (mainOrganization) selected = mainOrganization
        }

        if (!selected)
            Toast.danger(
                'We could not find your organization.',
                'You must be assigned to an organization to use the system.'
            )

        setSelectedOrganization(selected)
    }, [organizations, me])

    const [createUserLoading, createUserError, createUser] = useAction(
        async (data: TenantUserCreateDto) => {
            data.permissions = enrichUserPermissions(data.permissions)
            const response = await userApi.tenantUserCreateV2({
                tenantId: lazySelectedOrganizationId,
                tenantUserCreateDto: data,
            })
            const newUser = new User(response.data)
            throwIfResponseNotOk(response)
            await refreshUsers()
            await refreshUserDirectory()
            await refreshProfiles()
            return newUser
        }
    )

    const [updateUserLoading, updateUserError, updateUser] = useAction(
        async (userId: string, data: TenantUserUpdateDto) => {
            data.permissions = enrichUserPermissions(data.permissions ?? [])
            const response = await userApi.tenantUserUpdateV2({
                userIdpId: userId,
                tenantId: lazySelectedOrganizationId,
                tenantUserUpdateDto: data,
            })
            const updatedUser = new User(response.data)
            throwIfResponseNotOk(response)
            await refreshUsers()
            await refreshUserDirectory()
            await refreshProfiles()
            return updatedUser
        }
    )

    const [_archiveUserLoading, _archiveUserError, archiveUser] = useAction(
        async (userId?: string) => {
            if (!userId) return
            const response = await userApi.tenantUserArchiveV2({
                userIdpId: userId,
                tenantId: lazySelectedOrganizationId,
            })
            throwIfResponseNotOk(response)
            refreshUsers()
            refreshUserDirectory()
            Toast.completed('archive', 'user')
        }
    )

    const [_restoreUserLoading, _restoreUserError, restoreUser] = useAction(
        async (userId?: string) => {
            if (!userId) return
            const response = await userApi.tenantUserRestoreV2({
                userIdpId: userId,
                tenantId: lazySelectedOrganizationId,
            })
            throwIfResponseNotOk(response)
            refreshUsers()
            refreshUserDirectory()
            Toast.completed('restore', 'user')
        }
    )

    const [updateOrganizationLoading, updateOrganizationError, updateOrganization] = useAction(
        async (orgId: string, data: TenantUpdateDto) => {
            const response = await organizationApi.updateTenantV2({
                tenantId: orgId,
                tenantUpdateDto: data,
            })
            const updatedOrg = new Organization(response.data)
            throwIfResponseNotOk(response)
            replaceOrganization(updatedOrg)
            refreshAllOrganizations()
            return updatedOrg
        }
    )

    const resetPassword = async (userId: string) => {
        const response = await userApi.resetPasswordV2({
            userIdpId: userId,
            tenantId: lazySelectedOrganizationId,
        })
        return ok(response)
    }

    const [_archiveOrganizationLoading, _archiveOrganizationError, archiveOrganization] = useAction(
        async (organizationId?: string) => {
            if (!organizationId) return
            const response = await organizationApi.archiveTenantV2({ tenantId: organizationId })
            throwIfResponseNotOk(response)
            refreshOrganizations()
            refreshUsers()
            refreshUserDirectory()
            refreshAllOrganizations()
            Toast.completed('archive', 'organization')
        }
    )

    const [_restoreOrganizationLoading, _restoreOrganizationError, restoreOrganization] = useAction(
        async (organizationId?: string) => {
            if (!organizationId) return
            const response = await organizationApi.restoreTenantV2({ tenantId: organizationId })
            throwIfResponseNotOk(response)
            refreshOrganizations()
            refreshUsers()
            refreshUserDirectory()
            refreshAllOrganizations()
            Toast.completed('restore', 'organization')
        }
    )

    const [createOrganizationLoading, createOrganizationError, createOrganization] = useAction(
        async (data: TenantCreateDto) => {
            const response = await organizationApi.createTenantV2({ tenantCreateDto: data })
            const newOrganization = new Organization(response.data)
            throwIfResponseNotOk(response)
            addOrganization(newOrganization)
            refreshAllOrganizations()
            return newOrganization
        }
    )

    const selectOrganization = (id: string) => {
        const next = organizations?.find((org) => id === org.id)
        if (!next) return
        selectedOrganizationStorage.store(next.id)
        setSelectedOrganization(next)
    }

    const getUserNameById = useCallback(
        (id: string) => userDirectory?.[id] ?? t('common_unknown'),
        [userDirectory]
    )
    const getUserProfileById = useCallback(
        (id?: string) => allUserProfiles.items.find((u) => u.id === id),
        [allUserProfiles]
    )
    const getOrgProfileById = useCallback(
        (id?: string) =>
            allOrganizations.items.find((o) => o.id === id) ??
            organizations.find((o) => o.id === id),
        [allOrganizations, organizations]
    )

    const can = (permission: PermissionSuffix) =>
        !!me?.hasPermission(selectedOrganization?.id, permission)

    const loading = !me || !selectedOrganization
    const error = usersError

    useEffect(() => {
        if (error) Toast.error(error, 'User error')
    }, [error])

    const value = {
        me,
        meLoading,
        meError,
        refreshMe,
        can,
        loading,

        resetPassword,

        users,
        usersLoading,
        usersError,
        createUser,
        createUserLoading,
        createUserError,
        updateUser,
        updateUserLoading,
        updateUserError,
        archiveUser,
        restoreUser,

        userDirectory,
        getUserNameById,
        getUserProfileById,
        getOrgProfileById,

        organizations,
        organizationsLoading,
        organizationsError,
        lazySelectedOrganizationId,
        selectedOrganization,
        allOrganizations,
        allOrganizationsLoading,
        allOrganizationsError,
        createOrganization,
        createOrganizationLoading,
        createOrganizationError,
        updateOrganization,
        updateOrganizationLoading,
        updateOrganizationError,
        selectOrganization,
        archiveOrganization,
        restoreOrganization,
    }

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export const useUserContext = () => useProvideContext(UserContext, 'User')
